/**
 * Asteroid LOD selection (plan G7). Pure and allocation-free: runs for every rock at each re-bucketing.
 *
 * `thresholds` are distances per metre of rock radius, one per level, ascending: `thresholds[0]` is 0 (LOD0),
 * `thresholds[l]` is where level `l` switches in (`AsteroidKit.lodDistances`), and the LAST entry is the
 * cull level (the rock is not drawn at all; `CULLED_NONE` disables culling).
 */

/** A cull threshold that is never reached. */
export const CULLED_NONE = Number.POSITIVE_INFINITY

/**
 * The level for a rock at `distance` (m, camera to rock centre) with mean `radius` (m).
 * `previous` is the rock's current level (-1 = none yet, so no hysteresis). With hysteresis `h`, a switch
 * to a coarser level `l` needs `distance ≥ thresholds[l] × radius × (1 + h)` and a switch back to the finer
 * level below `l` needs `distance < thresholds[l] × radius × (1 − h)`, so a rock never flickers on a boundary.
 */
export function pickLodLevel(distance: number, radius: number, thresholds: Float64Array, previous: number, hysteresis: number): number {
  const last = thresholds.length - 1
  if (previous < 0 || previous > last) {
    let level = 0
    while (level < last && distance >= thresholds[level + 1] * radius) level++
    return level
  }
  let level = previous
  const up = 1 + hysteresis
  const down = 1 - hysteresis
  while (level < last && distance >= thresholds[level + 1] * radius * up) level++
  while (level > 0 && distance < thresholds[level] * radius * down) level--
  return level
}
